import { Alert, App, Button, Select, Space, Table, Tag } from 'antd';
import { Plus, RefreshCw, RotateCcw, Stethoscope, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { crestHttp } from '../../api/client.js';
import { EmptyState } from '../../components/ui/index.js';
import { DirectAccessGrantModal } from './DirectAccessGrantModal.js';
import type {
  AccessAssignmentRow,
  AccessScopeDimension,
  RoleCatalogRow,
  UserOption,
} from './access-center-types.js';

const STATE_COLORS: Record<string, string> = {
  active: 'green',
  approval_pending: 'processing',
  approval_failed: 'red',
  approval_rejected: 'default',
  approval_cancelled: 'default',
  applying: 'processing',
  retrying: 'processing',
  failed: 'red',
  revoke_pending: 'orange',
  revoked: 'default',
  expired: 'default',
  pending: 'gold',
};

export function AccessAssignmentsPanel() {
  const { t } = useTranslation('accessCenter');
  const { message, modal } = App.useApp();
  const [searchParams] = useSearchParams();
  const selectedUserId = searchParams.get('userId') || undefined;
  const [rows, setRows] = useState<AccessAssignmentRow[]>([]);
  const [roles, setRoles] = useState<RoleCatalogRow[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [state, setState] = useState<string>();
  const [health, setHealth] = useState<Record<string, unknown> | null>(null);
  const [orchestrationMode, setOrchestrationMode] = useState<'off' | 'shadow' | 'enforce'>('off');
  const grantableRoles = useMemo(
    () =>
      roles.filter(
        (role) =>
          !['system', 'admin'].includes(role.role_type) &&
          role.metadata?.requiresApproval !== true &&
          role.metadata?.hasGlobalAccess !== true &&
          role.metadata?.riskLevel !== 'high',
      ),
    [roles],
  );

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      crestHttp.get<{
        rows: AccessAssignmentRow[];
        mode: 'off' | 'shadow' | 'enforce';
      }>('/admin/access/assignments', {
        params: { pageSize: 200, state, userId: selectedUserId },
      }),
      crestHttp.get<{ rows: RoleCatalogRow[] }>('/admin/access/roles', {
        params: { pageSize: 500, status: 'active' },
      }),
      crestHttp.get<{ rows: UserOption[] }>('/admin/users', {
        params: { pageSize: 500 },
      }),
    ])
      .then(([assignments, catalog, userRows]) => {
        setRows(assignments.data.rows ?? []);
        setOrchestrationMode(assignments.data.mode ?? 'off');
        setRoles(catalog.data.rows ?? []);
        setUsers(userRows.data.rows ?? []);
      })
      .catch(() => message.error(t('loadFailed')))
      .finally(() => setLoading(false));
  }, [message, selectedUserId, state, t]);
  useEffect(() => load(), [load]);

  const mutate = async (row: AccessAssignmentRow, action: 'retry' | 'revoke') => {
    await crestHttp.post(`/admin/access/assignments/${row.access_assignment_id}/${action}`);
    message.success(t(action === 'retry' ? 'retryQueued' : 'revoked'));
    load();
  };

  const checkHealth = async () => {
    const { data } = await crestHttp.get<Record<string, unknown>>(
      '/admin/access/projection/health',
    );
    setHealth(data);
  };

  const reconcile = async () => {
    await crestHttp.post('/admin/access/projection/reconcile');
    message.success(t('reconcileDone'));
    await checkHealth();
  };

  return (
    <div className="crest-list-card">
      {orchestrationMode !== 'enforce' && (
        <Alert
          showIcon
          type="error"
          message={t('orchestrationNotEnforced')}
          description={t('orchestrationNotEnforcedDescription', {
            mode: orchestrationMode,
          })}
          style={{ marginBottom: 12 }}
        />
      )}
      {health && (
        <Alert
          showIcon
          closable
          onClose={() => setHealth(null)}
          type={health.healthy ? 'success' : 'warning'}
          message={health.healthy ? t('projectionHealthy') : t('projectionMismatch')}
          description={t('projectionCounts', {
            missing: health.membersWithoutSource ?? 0,
            extra: health.activeSourcesWithoutMember ?? 0,
            failed: health.failed ?? 0,
          })}
          action={
            <Button size="small" disabled={orchestrationMode !== 'enforce'} onClick={reconcile}>
              {t('reconcile')}
            </Button>
          }
          style={{ marginBottom: 12 }}
        />
      )}
      <Space wrap style={{ marginBottom: 12 }}>
        <Button
          type="primary"
          icon={<Plus size={14} />}
          disabled={orchestrationMode !== 'enforce'}
          onClick={() => setCreateOpen(true)}
        >
          {t('directGrant')}
        </Button>
        <Select
          allowClear
          placeholder={t('allStates')}
          value={state}
          onChange={setState}
          style={{ width: 160 }}
          options={Object.keys(STATE_COLORS).map((value) => ({
            value,
            label: t(`state_${value}`),
          }))}
        />
        <Button icon={<RefreshCw size={14} />} onClick={load}>
          {t('common:refresh')}
        </Button>
        <Button icon={<Stethoscope size={14} />} onClick={checkHealth}>
          {t('projectionHealth')}
        </Button>
      </Space>
      <Table
        rowKey="access_assignment_id"
        loading={loading}
        dataSource={rows}
        locale={{
          emptyText: <EmptyState title={t('noAssignments')} compact />,
        }}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        columns={[
          { title: t('user'), render: (_, row) => row.user?.fullname ?? '-' },
          {
            title: t('roleName'),
            render: (_, row) =>
              row.role ? `[${row.role.domain.toUpperCase()}] ${row.role.name}` : '-',
          },
          { title: t('source'), dataIndex: 'source_type', width: 110 },
          {
            title: t('dataScope'),
            width: 280,
            render: (_, row) => (
              <span>
                {t(`scope_${row.scope_policy ?? 'role_default'}`)}
                {row.scope_config?.schemaVersion === 2 && row.scope_config.businessOrg
                  ? ` · ${t('businessOrgScope')}: ${scopeDimensionLabel(row.scope_config.businessOrg, t)}`
                  : ''}
                {row.scope_config?.schemaVersion === 2 && row.scope_config.salesRegion
                  ? ` · ${t('salesRegionScope')}: ${scopeDimensionLabel(row.scope_config.salesRegion, t)}`
                  : ''}
                {row.scope_config?.scopeIds?.length
                  ? ` · ${t('scopeCount', { count: row.scope_config.scopeIds.length })}`
                  : ''}
              </span>
            ),
          },
          {
            title: t('common:status'),
            dataIndex: 'state',
            width: 130,
            render: (value) => <Tag color={STATE_COLORS[value]}>{t(`state_${value}`)}</Tag>,
          },
          {
            title: t('validFrom'),
            dataIndex: 'valid_from',
            width: 180,
            render: (value) => new Date(value).toLocaleString(),
          },
          {
            title: t('validTo'),
            dataIndex: 'valid_to',
            width: 180,
            render: (value) => (value ? new Date(value).toLocaleString() : t('indefinite')),
          },
          {
            title: t('actions'),
            width: 150,
            render: (_, row) => (
              <Space size={0}>
                {row.state === 'failed' && (
                  <Button
                    type="link"
                    icon={<RotateCcw size={13} />}
                    disabled={orchestrationMode !== 'enforce'}
                    onClick={() => mutate(row, 'retry')}
                  >
                    {t('retry')}
                  </Button>
                )}
                {![
                  'revoked',
                  'expired',
                  'revoke_pending',
                  'approval_rejected',
                  'approval_cancelled',
                ].includes(row.state) && (
                  <Button
                    type="link"
                    danger
                    icon={<XCircle size={13} />}
                    disabled={orchestrationMode !== 'enforce'}
                    onClick={() =>
                      modal.confirm({
                        title: t('revokeConfirm'),
                        onOk: () => mutate(row, 'revoke'),
                      })
                    }
                  >
                    {t('revoke')}
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
      />
      <DirectAccessGrantModal
        open={createOpen}
        roles={grantableRoles}
        users={users}
        userId={selectedUserId}
        onCancel={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          load();
        }}
      />
    </div>
  );
}

function scopeDimensionLabel(
  dimension: AccessScopeDimension,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (dimension.mode !== 'selected') return t(`scopeMode_${dimension.mode}`);
  return t('scopeNodeCount', { count: dimension.scopeIds.length });
}
